// Contas da tela da Gestão do Financeiro (#6499): Meyryene.
//
// - Faturamento no prazo: das faturas do mês já faturadas (Conferência do
//   Faturamento), quantas saíram até o limite de emissão.
// - Cobrança do dia por pessoa: a régua de contato humano (D+4, D+10, D+17, D+24,
//   tabela `cobranca_acao_manual`) de cada responsável — o que vence hoje, o que
//   já passou do dia e o que a pessoa resolveu hoje.
// - Parcelas de acordo da semana: o que vence nos próximos 7 dias.
//
// Lógica pura.

import type { EstadoPrazo, SituacaoFaturamento } from '../conferenciaFaturamento.ts';

// ── Faturamento no prazo ──

export interface PrazoDoFaturamento {
  /** Faturadas com prazo calculável. */
  total: number;
  noPrazo: number;
  foraDoPrazo: number;
  aposVencimento: number;
  /** noPrazo ÷ total; null sem nenhuma faturada. */
  fracao: number | null;
}

export function prazoDoFaturamento(linhas: readonly { situacao: SituacaoFaturamento; estadoPrazo: EstadoPrazo }[]): PrazoDoFaturamento {
  let noPrazo = 0, foraDoPrazo = 0, aposVencimento = 0;
  for (const l of linhas) {
    if (l.situacao !== 'faturado') continue;
    if (l.estadoPrazo === 'no_prazo') noPrazo++;
    else if (l.estadoPrazo === 'fora_do_prazo') foraDoPrazo++;
    else if (l.estadoPrazo === 'apos_vencimento') aposVencimento++;
  }
  const total = noPrazo + foraDoPrazo + aposVencimento;
  return { total, noPrazo, foraDoPrazo, aposVencimento, fracao: total ? noPrazo / total : null };
}

// ── Régua de cobrança por pessoa ──

export interface AcaoDaRegua {
  etapa: string;
  /** Vencimento do boleto que originou a etapa ('AAAA-MM-DD'). */
  data_referencia: string;
  status: string;
  responsavel_id: string | null;
  executado_por: string | null;
  executado_em: string | null;
}

const somarDias = (iso: string, dias: number) => {
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number);
  const dt = new Date(a, m - 1, d + dias);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

/** Dia em que a etapa deve ser feita: vencimento + N de "D+N". */
export function diaDaAcao(a: Pick<AcaoDaRegua, 'etapa' | 'data_referencia'>): string {
  const n = Number(/^D\+(\d+)$/.exec(a.etapa)?.[1] ?? 0);
  return somarDias(a.data_referencia, n);
}

/** Data local ('AAAA-MM-DD') de um instante ISO. */
const diaLocal = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export interface ReguaDaPessoa {
  /** null = sem responsável. */
  responsavelId: string | null;
  /** Pendentes cujo dia é hoje. */
  hoje: number;
  /** Pendentes cujo dia já passou. */
  atrasadas: number;
  /** Feitas ou dispensadas hoje pela pessoa. */
  feitasHoje: number;
  /** Dia da pendência mais antiga. */
  maisAntiga: string | null;
}

/** Uma linha por pessoa com algo pendente ou feito hoje; mais atrasadas primeiro. */
export function reguaPorPessoa(acoes: readonly AcaoDaRegua[], hojeIso: string): ReguaDaPessoa[] {
  const porPessoa = new Map<string | null, ReguaDaPessoa>();
  const linha = (id: string | null) => {
    let r = porPessoa.get(id);
    if (!r) { r = { responsavelId: id, hoje: 0, atrasadas: 0, feitasHoje: 0, maisAntiga: null }; porPessoa.set(id, r); }
    return r;
  };
  for (const a of acoes) {
    if (a.status === 'pendente') {
      const dia = diaDaAcao(a);
      if (dia > hojeIso) continue;
      const r = linha(a.responsavel_id);
      if (dia === hojeIso) r.hoje++;
      else r.atrasadas++;
      if (!r.maisAntiga || dia < r.maisAntiga) r.maisAntiga = dia;
    } else if (a.executado_em && diaLocal(a.executado_em) === hojeIso) {
      linha(a.executado_por ?? a.responsavel_id).feitasHoje++;
    }
  }
  return [...porPessoa.values()].sort((x, y) => y.atrasadas - x.atrasadas || y.hoje - x.hoje || y.feitasHoje - x.feitasHoje);
}

// ── Acordos ──

export interface ParcelaDeAcordo {
  data_vencimento: string | null;
  valor: number | string | null;
  status: string | null;
}

/** Parcelas ainda não pagas que vencem de hoje a 6 dias à frente. */
export function parcelasDaSemana(parcelas: readonly ParcelaDeAcordo[], hojeIso: string): { qtd: number; valor: number } {
  const fim = somarDias(hojeIso, 6);
  let qtd = 0, valor = 0;
  for (const p of parcelas) {
    const venc = String(p.data_vencimento ?? '').slice(0, 10);
    if (!venc || venc < hojeIso || venc > fim) continue;
    if (p.status !== 'pendente' && p.status !== 'provisionado') continue;
    qtd++;
    valor += Number(p.valor) || 0;
  }
  return { qtd, valor };
}

// ── Recuperação da carteira vencida (#6506) ──

export const META_RECUPERACAO_CARTEIRA = 0.3;

export interface FaturaParaEstoque {
  statusDerivado: string;
  vencimento: string | null;
  data_pgto_boleto: string | null;
  valor: number;
}

/**
 * Valor vencido em aberto numa data: boleto de cobrança vencido antes dela que, naquela
 * data, ainda não tinha sido pago (hoje segue vencido, ou foi pago na data ou depois).
 * Parcelado, cancelado e quitado por outra cobrança ficam de fora, como no painel.
 */
export function estoqueVencidoEm(faturas: readonly FaturaParaEstoque[], dataIso: string): { valor: number; boletos: number } {
  let valor = 0, boletos = 0;
  for (const f of faturas) {
    const venc = (f.vencimento ?? '').slice(0, 10);
    if (!venc || venc >= dataIso) continue;
    const abertoNaData = f.statusDerivado === 'Vencido'
      || (f.statusDerivado === 'Pago' && !!f.data_pgto_boleto && diaLocal(f.data_pgto_boleto) >= dataIso);
    if (!abertoNaData) continue;
    valor += f.valor;
    boletos++;
  }
  return { valor, boletos };
}
